import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Key, X } from "@phosphor-icons/react";
import { api, errDetail } from "../lib/api";
import { Sidebar } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [usage, setUsage] = useState(null);
  const [capInput, setCapInput] = useState("");

  const loadUsage = () => api.get("/admin/usage").then((r) => { setUsage(r.data); setCapInput(String(r.data.cap)); }).catch(() => {});

  const saveCap = async () => {
    try {
      await api.put("/admin/usage/cap", { cap: parseFloat(capInput) });
      toast.success(`Monthly cap set to $${parseFloat(capInput).toFixed(0)}`);
      loadUsage();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); loadUsage(); }, []);

  const addUser = async () => {
    try {
      await api.post("/admin/users", { ...form, role: "member" });
      toast.success("User created");
      setShowAdd(false);
      setForm({ email: "", name: "", password: "" });
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const resetPassword = async () => {
    try {
      await api.put(`/admin/users/${resetTarget.id}`, { password: newPassword });
      toast.success(`Password updated for ${resetTarget.email}`);
      setResetTarget(null);
      setNewPassword("");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const inputCls = "w-full bg-[var(--bg)] border border-[var(--border-strong)] rounded-xl px-3.5 py-2.5 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] transition-colors duration-300 placeholder:text-[var(--muted)]";

  return (
    <div className="flex h-screen bg-[var(--bg)] overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto px-10 py-10">
        <h1 className="font-serif-display font-semibold text-3xl sm:text-4xl tracking-tight mb-2">Team</h1>
        <p className="text-sm text-[var(--text-2)] mb-8">Add teammates, reset passwords, manage access & spending.</p>

        {usage && (
          <section className="max-w-3xl mb-10" data-testid="usage-section">
            <h2 className="font-serif-display font-semibold text-lg mb-4">Usage & Costs <span className="text-[11px] font-sans font-normal text-[var(--muted)]">(estimated, this month)</span></h2>
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-5 mb-4" style={{ boxShadow: "var(--shadow)" }}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <p className="text-sm" data-testid="usage-month-total">
                  <span className="font-semibold text-[var(--text)]">${usage.month_total.toFixed(2)}</span>
                  <span className="text-[var(--text-2)]"> of ${usage.cap.toFixed(0)} monthly cap</span>
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--text-2)]">Cap $</span>
                  <input data-testid="cap-input" type="number" min="1" value={capInput} onChange={(e) => setCapInput(e.target.value)}
                    className="w-24 bg-[var(--bg)] border border-[var(--border-strong)] rounded-xl px-3 py-1.5 text-sm outline-none focus:border-[var(--accent)] transition-colors duration-300" />
                  <button data-testid="save-cap-button" onClick={saveCap}
                    className="rounded-full text-white text-xs font-semibold px-4 py-1.5 transition-colors duration-300" style={{ background: "var(--accent)" }}>Set</button>
                </div>
              </div>
              <div className="h-2.5 rounded-full bg-[var(--surface-2)] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" data-testid="usage-progress-bar"
                  style={{ width: `${Math.min(100, (usage.month_total / usage.cap) * 100)}%`,
                    background: usage.month_total / usage.cap >= 1 ? "var(--error)" : usage.month_total / usage.cap >= 0.8 ? "#D97706" : "var(--accent)" }} />
              </div>
              <p className="text-[11px] text-[var(--muted)] mt-2">When the cap is reached, chat & image generation pause for everyone until next month or a higher cap.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden" data-testid="usage-by-user">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)] bg-[var(--surface-2)] px-4 py-2.5">Per user</p>
                {usage.by_user.length === 0 && <p className="text-xs text-[var(--muted)] px-4 py-3">No usage yet this month.</p>}
                {usage.by_user.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between px-4 py-2.5 border-t border-[var(--border)]">
                    <span className="text-[13px] text-[var(--text)] truncate">{u.name}</span>
                    <span className="text-[12px] text-[var(--text-2)] shrink-0">today ${u.today.toFixed(2)} · month <b className="text-[var(--text)]">${u.month.toFixed(2)}</b></span>
                  </div>
                ))}
              </div>
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden" data-testid="usage-by-model">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)] bg-[var(--surface-2)] px-4 py-2.5">Per model</p>
                {usage.by_model.length === 0 && <p className="text-xs text-[var(--muted)] px-4 py-3">No usage yet this month.</p>}
                {usage.by_model.map((m, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 border-t border-[var(--border)]">
                    <span className="text-[13px] text-[var(--text)] truncate">{m.model}</span>
                    <span className="text-[12px] text-[var(--text-2)] shrink-0">{m.calls} calls · <b className="text-[var(--text)]">${m.month.toFixed(2)}</b></span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <button data-testid="add-user-button" onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-2 text-[13px] font-semibold text-white rounded-full px-4 py-2 mb-6 transition-colors duration-300" style={{ background: "var(--accent)" }}>
          <Plus size={14} weight="bold" /> Add Member
        </button>

        {showAdd && (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-5 mb-6 max-w-md space-y-3 scale-in" data-testid="add-user-form" style={{ boxShadow: "var(--shadow)" }}>
            <input data-testid="new-user-name" className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="new-user-email" className={inputCls} placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="new-user-password" className={inputCls} placeholder="Password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <p className="text-[11px] text-[var(--muted)]">All new members get standard access. Only the primary admin manages the team.</p>
            <button data-testid="create-user-button" onClick={addUser}
              className="rounded-full text-white text-[13px] font-semibold px-5 py-2 transition-colors duration-300" style={{ background: "var(--accent)" }}>Create</button>
          </div>
        )}

        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl max-w-3xl overflow-x-auto" data-testid="users-table">
          <div className="min-w-[560px]">
          <div className="grid grid-cols-[1fr_1.5fr_100px_90px] text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)] bg-[var(--surface-2)] px-5 py-3 rounded-t-2xl">
            <span>Name</span><span>Email</span><span>Role</span><span className="text-right">Actions</span>
          </div>
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-[1fr_1.5fr_100px_90px] items-center px-5 py-3.5 border-t border-[var(--border)] hover:bg-[var(--hover)] transition-colors duration-300">
              <span className="text-sm font-medium text-[var(--text)] truncate">{u.name}</span>
              <span className="text-[13px] text-[var(--text-2)] truncate">{u.email}</span>
              <span>
                <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${u.role === "admin" ? "text-white" : "bg-[var(--surface-2)] text-[var(--text-2)]"}`} style={u.role === "admin" ? { background: "var(--accent)" } : {}}>{u.role}</span>
              </span>
              <span className="flex justify-end gap-1">
                <button data-testid={`reset-password-${u.id}`} title="Reset password" onClick={() => setResetTarget(u)}
                  className="text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><Key size={15} /></button>
                {u.id !== user?.id && (
                  <button data-testid={`delete-user-${u.id}`} title="Delete user" onClick={() => deleteUser(u.id)}
                    className="text-[var(--muted)] hover:text-[var(--error)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><Trash size={15} /></button>
                )}
              </span>
            </div>
          ))}
          </div>
        </div>

        {resetTarget && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" data-testid="reset-password-modal">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-6 w-full max-w-sm scale-in" style={{ boxShadow: "var(--shadow)" }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif-display font-semibold text-lg">Reset Password</h2>
                <button onClick={() => setResetTarget(null)} className="text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><X size={16} /></button>
              </div>
              <p className="text-[13px] text-[var(--text-2)] mb-3">{resetTarget.email}</p>
              <input data-testid="reset-password-input" className={inputCls} placeholder="New password" type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              <button data-testid="confirm-reset-password" onClick={resetPassword} disabled={!newPassword}
                className="mt-4 w-full rounded-full text-white text-[13px] font-semibold py-2.5 transition-colors duration-300 disabled:opacity-40" style={{ background: "var(--accent)" }}>Update</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
